import { useEffect, useRef } from "react";

/**
 * What Back undoes, in one place.
 *
 * THE RULE (owner, 2026-09-22): "it's always supposed to go to the previous
 * state or page first". Back undoes the last thing that happened, one step at a
 * time, and only leaves the app when there is genuinely nothing left to undo:
 *
 *   a menu, a dialog or a detail card is open  -> close it
 *   the multiview grid is open                 -> close the grid
 *   full screen                                -> leave full screen, onto the
 *                                                 page you played from
 *   otherwise                                  -> the previous page
 *   the first page, with a video playing        -> picture-in-picture (the APK
 *                                                 does this in MainActivity)
 *   the first page, nothing playing            -> leave the app
 *
 * WHY THIS EXISTS. Before it, exactly three things in the app knew that Back
 * should undo them — full screen, the player's gear menu and the multiview
 * grid — and each carried its own copy of the same twenty lines. No page, no
 * detail card and no dialog took part at all, so Back on an open movie card
 * navigated away or closed the app. Every new screen was a fresh chance to
 * forget. Screens now register instead of remembering.
 *
 * HOW. Each open layer pushes one history entry carrying its own id, so the
 * platform's Back reaches us the same way on the phone, a TV remote and the
 * web. One listener, here, owns the popping.
 *
 * THE BUG THIS ALSO FIXES (measured on the projector, 2026-09-22). All three
 * copies pushed `{ ...history.state, marker: true }`, which COPIES React
 * Router's own `idx` — so two entries claimed to be router index 1. The router
 * computes `delta = nextIndex - index` on a pop, got 1 - 1 = 0, and concluded
 * nothing had moved: the first Back out of full screen did nothing at all.
 * Worse, its next push seeds `index` from that copy, so the router's position
 * drifts out of step with the browser's for good — which is why Back landed on
 * Home instead of the Movies page, and why three presses closed the app when it
 * should have taken four. An entry pushed here carries the NEXT index, never a
 * copy of the current one.
 */

/** The field our entries carry. Anything else in history is the router's. */
const LAYER_KEY = "slqBackLayer";

interface Layer {
  id: number;
  onBack: () => void;
}

let layers: Layer[] = [];
let nextId = 1;
let listening = false;
let depthListeners: Array<(depth: number) => void> = [];
/** A closed layer whose entry is still underfoot, waiting to be unwound (scheduleUnwind). */
let pendingUnwind: number | null = null;
/**
 * The layer an in-flight `history.back()` is stepping off, until its popstate
 * lands (scheduleUnwind).
 *
 * THE BUG (projector, 2026-09-27): Go Live by OK failed about one time in two.
 * The card's Back was still travelling when full screen opened — 1.25 s later
 * on the busy projector, far too late for the same-render hand-over below — so
 * full screen pushed its entry first, the Back landed underneath it, and full
 * screen closed 135 ms after it appeared. A layer opened while a Back is in
 * flight now waits for the landing before it puts its entry in, and that
 * landing closes nothing.
 */
let unwinding: number | null = null;
/** Layers opened while that Back was in flight; their entries go in once it lands. */
let deferredPushes: number[] = [];
let unwindSafety: ReturnType<typeof setTimeout> | null = null;

interface HistoryState {
  idx?: number;
  [LAYER_KEY]?: number;
}

function state(): HistoryState | null {
  return (history.state ?? null) as HistoryState | null;
}

/** The layer the current history entry belongs to, or null for a router entry. */
function currentLayerId(): number | null {
  const id = state()?.[LAYER_KEY];
  return typeof id === "number" ? id : null;
}

/**
 * How many steps Back has left before it would leave the app.
 *
 * Every entry advances `idx` — the router's pushes and this file's layer
 * entries alike — so the router index IS the number of things left to undo.
 * Zero means the first screen with nothing open.
 */
export function backDepth(): number {
  return state()?.idx ?? 0;
}

/**
 * Tell anyone watching that the depth may have changed.
 *
 * The APK is the one that listens. Android's Back arrives in Java, which has
 * to decide between stepping back inside the page and leaving the app, and it
 * cannot ask the page a question and wait for the answer. So the page keeps it
 * informed instead, and Java reads the last number it was told.
 */
function announce(): void {
  const depth = backDepth();
  for (const listener of depthListeners) listener(depth);
}

/**
 * Watch the number of steps Back has left. Fires on every entry this file
 * pushes and on every pop; a router navigation is announced by the app, which
 * is the only side that can see one (`notifyBackDepth`).
 */
export function onBackDepth(listener: (depth: number) => void): () => void {
  depthListeners.push(listener);
  listener(backDepth());
  return () => {
    depthListeners = depthListeners.filter((other) => other !== listener);
  };
}

/** Announce a depth change this file did not cause — a router navigation. */
export function notifyBackDepth(): void {
  announce();
}

/**
 * Push an entry for a layer.
 *
 * `idx` is the router's, and it must ADVANCE — see the note above. Everything
 * else in the current state is carried so the router keeps its `key` and
 * whatever it stores against this location.
 */
function pushLayerEntry(id: number): void {
  const current = state();
  history.pushState({ ...current, idx: (current?.idx ?? 0) + 1, [LAYER_KEY]: id }, "");
  announce();
}

/** Our own Back has landed (or never will): put in the entries that waited for it. */
function finishUnwind(): void {
  if (unwindSafety !== null) clearTimeout(unwindSafety);
  unwindSafety = null;
  unwinding = null;
  const waiting = deferredPushes;
  deferredPushes = [];
  for (const id of waiting) if (layers.some((layer) => layer.id === id)) pushLayerEntry(id);
}

function onPopState(): void {
  // Our own unwind landing, not the viewer pressing Back. The layer it stepped
  // off is already gone, so it closes nothing — least of all a layer that
  // opened while it travelled.
  if (unwinding !== null) {
    finishUnwind();
    announce();
    return;
  }
  // Everything above the entry we landed on is now closed. Run them from the
  // top down, so a card that opened a dialog closes the dialog first.
  const landed = currentLayerId();
  const keep: Layer[] = [];
  const closed: Layer[] = [];
  for (const layer of layers) {
    if (landed !== null && layer.id <= landed) keep.push(layer);
    else closed.push(layer);
  }
  layers = keep;
  announce();
  for (const layer of closed.reverse()) layer.onBack();
}

function listen(): void {
  if (listening || typeof window === "undefined") return;
  listening = true;
  window.addEventListener("popstate", onPopState);
}

/**
 * Open a layer: Back will close it before anything underneath.
 *
 * Returns the function to close it from the app's own UI (an X button, Escape).
 * That goes back through history too, so the entry is consumed rather than left
 * for a later Back to land on.
 */
export function openBackLayer(onBack: () => void): () => void {
  listen();
  const id = nextId++;
  layers.push({ id, onBack });
  if (unwinding !== null) {
    // A Back is still travelling; pushing now would put this entry where it lands.
    deferredPushes.push(id);
  } else if (pendingUnwind !== null && currentLayerId() === pendingUnwind) {
    // A layer closed in this same render and another opened in its place (the
    // quick menu's "Programme details", "Record…"). Take over its entry rather
    // than stacking one on top of an entry about to be unwound — see
    // scheduleUnwind. Back from the new layer then lands where the old one
    // would have: the menu is gone, so it is not reopened.
    pendingUnwind = null;
    history.replaceState({ ...state(), [LAYER_KEY]: id }, "");
    announce();
  } else {
    pushLayerEntry(id);
  }
  return () => {
    if (!layers.some((layer) => layer.id === id)) return;
    layers = layers.filter((layer) => layer.id !== id);
    // Closed before its entry went in: there is nothing to put in, or undo.
    deferredPushes = deferredPushes.filter((waiting) => waiting !== id);
    // Only unwind history if our entry is still the one we are standing on;
    // if Back already consumed it, there is nothing to undo.
    if (currentLayerId() === id) scheduleUnwind(id);
  };
}

/**
 * Step back off a closed layer's entry — after the current render, not during it.
 *
 * THE BUG (projector, 2026-09-24): `history.back()` is asynchronous. When one
 * layer closes and another opens in the same render — the menu choosing
 * "Programme details" — the new layer pushes its entry first and the Back
 * lands afterwards, on the NEW entry, closing the dialog the viewer just asked
 * for. It opened and vanished in the same instant. React's development mode
 * does the same to every dialog (StrictMode mounts effects twice), which is
 * how it showed up in the preview.
 *
 * Deferring to a microtask lets every effect of the render run first; a layer
 * opened meanwhile adopts the entry (openBackLayer) and cancels the unwind.
 */
function scheduleUnwind(id: number): void {
  pendingUnwind = id;
  queueMicrotask(() => {
    if (pendingUnwind !== id) return;
    pendingUnwind = null;
    if (currentLayerId() !== id) return;
    unwinding = id;
    history.back();
    // A Back that never lands must not hold new layers' entries for ever.
    unwindSafety = setTimeout(finishUnwind, 1000);
  });
}

/**
 * Register a dismissable layer for as long as `open` is true.
 *
 * ```ts
 * useBackLayer(detailOpen, () => setDetailOpen(false));
 * ```
 *
 * `onBack` is read through a ref, so a caller may pass a fresh closure on every
 * render without re-registering the layer.
 */
export function useBackLayer(open: boolean, onBack: () => void): void {
  const handler = useRef(onBack);
  handler.current = onBack;
  useEffect(() => {
    if (!open) return;
    const close = openBackLayer(() => handler.current());
    return () => close();
    // The handler is held in the ref above; re-registering on every render
    // would push a history entry per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

/** True while anything is registered — the APK asks before leaving the app. */
export function hasBackLayers(): boolean {
  return layers.length > 0;
}

/** Tests only: forget every layer without touching history. */
export function resetBackLayers(): void {
  layers = [];
  nextId = 1;
  pendingUnwind = null;
  if (unwindSafety !== null) clearTimeout(unwindSafety);
  unwindSafety = null;
  unwinding = null;
  deferredPushes = [];
  depthListeners = [];
}
